import * as React from 'react'
import { connect } from 'react-redux'

import { creditCardOfferPreviewSelected, stepSelected } from 'common/actions'
import { filteredRatesSelector } from 'embed/selectors'
import { MultiProductDispatch, OfferOverflowList } from 'embed/constants'
import { CreditOffer } from 'embed/constants/models'
import { CCIntroVsPreviewVsHybridOfferList, PreSelectApplicationForm, withExperiment } from 'embed/experiments'

import { ConnectedCreditFilterContainer } from 'embed/components/CreditFilter/container'
import PaginateOffers from 'embed/components/PaginateOffers'
import { brandSelector } from 'embed/selectors/config'
import { Brand, CreditCardFlow, CreditCardStep, Product, ProductResponseKeys } from 'even-ts-static'

import { MultiProductState } from 'embed/reducers'
import { uiPreviewIsInitializingSelector } from 'embed/selectors/ui'
import { CreditDisclosure } from '../CreditDisclosure'
import { ConnectedCreditOfferListContainer } from '../CreditOfferList/container'

interface DispatchProps {
  dispatchOfferPreviewSelected: (offer: CreditOffer, openInNewTab: boolean, flow?: CreditCardFlow) => void
  dispatchStepSelected: (product: Product, step: CreditCardStep) => void
}

interface StoreProps {
  brand: Brand
  creditOffers: CreditOffer[]
  leadUuid: string
  loadingPredicate: boolean
  offerOverflow: string | undefined
  pageSize: number | undefined
  rateTableUuid: string
  selectedPreSelectApplicationFormVariantId?: string
  selectedCCIntroVsPreviewVsHybridOfferListVariantId?: string
}

type Props = DispatchProps & StoreProps

export const CreditCardPreviewView: React.FunctionComponent<Props> = ({
  brand,
  creditOffers,
  dispatchOfferPreviewSelected,
  dispatchStepSelected,
  offerOverflow,
  pageSize,
  selectedPreSelectApplicationFormVariantId,
  selectedCCIntroVsPreviewVsHybridOfferListVariantId,
  ...other
}) => {
  React.useEffect(() => {
    if (
      selectedPreSelectApplicationFormVariantId === PreSelectApplicationForm.variants.shouldStartOnApplicationStep.id
    ) {
      dispatchStepSelected(Product.CreditCard, CreditCardStep.PreSelectForm)
    } else {
      dispatchStepSelected(Product.CreditCard, CreditCardStep.Preview)
    }
  }, [])
  const flow =
    selectedCCIntroVsPreviewVsHybridOfferListVariantId === CCIntroVsPreviewVsHybridOfferList.variants.Preview.id
      ? CreditCardFlow.Redirect
      : undefined
  const onPreviewSelected = (offer: CreditOffer) => dispatchOfferPreviewSelected(offer, false, flow)

  const offerListRef = React.useRef(null)

  return (
    <PaginateOffers
      enabled={offerOverflow === OfferOverflowList.PAGINATE}
      offerListRef={offerListRef}
      offers={creditOffers}
      pageSize={pageSize}
    >
      {({ offersPage, paginationControls }) => (
        <div>
          <ConnectedCreditFilterContainer />
          <ConnectedCreditOfferListContainer
            creditOffers={offersPage as CreditOffer[]}
            handleOfferCta={onPreviewSelected}
            offerListRef={offerListRef}
            {...other}
          />
          {paginationControls}
          <CreditDisclosure brand={brand} />
        </div>
      )}
    </PaginateOffers>
  )
}

export const mapStateToProps = (state: MultiProductState): StoreProps => ({
  brand: brandSelector(state),
  creditOffers: filteredRatesSelector(state, {
    offersPath: `${Product.CreditCard}.${ProductResponseKeys[Product.CreditCard]}Offers`,
    product: Product.CreditCard,
  }),
  leadUuid: state.rates.credit_card.leadUuid,
  loadingPredicate: uiPreviewIsInitializingSelector(state),
  offerOverflow: state.config.offer_overflow,
  pageSize: state.config.page_size,
  rateTableUuid: state.rates.credit_card.uuid,
})

const mapDispatchToProps = (dispatch: MultiProductDispatch): DispatchProps => ({
  dispatchOfferPreviewSelected: (offer: CreditOffer, openInNewTab: boolean, flow?: CreditCardFlow) =>
    dispatch(creditCardOfferPreviewSelected(offer, { openInNewTab, flow })),
  dispatchStepSelected: (product: Product, step: CreditCardStep) => dispatch(stepSelected(product, step)),
})

export const ConnectedCreditCardPreview = connect(mapStateToProps, mapDispatchToProps)(CreditCardPreviewView)

export const CreditCardPreview = withExperiment(PreSelectApplicationForm, {
  selectedVariantPropName: 'selectedPreSelectApplicationFormVariantId',
})(
  withExperiment(CCIntroVsPreviewVsHybridOfferList, {
    selectedVariantPropName: 'selectedCCIntroVsPreviewVsHybridOfferListVariantId',
  })(ConnectedCreditCardPreview),
)
